import React from 'react';
import { Artifact } from '../../../types/artifact';
import { useTheme } from '../../../contexts/ThemeContext';

interface CritiqueArtifactViewerProps {
  artifact: Artifact;
}

export const CritiqueArtifactViewer: React.FC<CritiqueArtifactViewerProps> = ({ artifact }) => {
  const { darkMode } = useTheme();

  const getCritiqueContent = () => {
    if (typeof artifact.content === 'string') return { content: artifact.content, score: 0 };
    return artifact.content;
  };

  const critique = getCritiqueContent();

  const renderScore = (score: number) => (
    <div className="flex">
      {[1, 2, 3, 4, 5].map((star) => (
        <span key={star} className={`text-lg ${star <= score ? 'text-yellow-500' : darkMode ? 'text-gray-600' : 'text-gray-300'}`}>
          ★
        </span>
      ))}
      <span className={`ml-2 text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
        {score}/5
      </span>
    </div>
  );

  const sectionList = (items: string[], title: string) => (
    <div className="mt-4">
      <h4 className={`text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{title}</h4>
      <ul className={`text-sm list-disc pl-5 space-y-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
        {items.map((item: string, index: number) => <li key={index}>{item}</li>)}
      </ul>
    </div>
  );

  return (
    <div className="critique-artifact-viewer">
      <div className={`border rounded-lg p-6 ${
        darkMode ? 'bg-red-900/20 border-red-700' : 'bg-red-50 border-red-200'
      }`}>
        <div className="flex justify-between items-start mb-4">
          <div className="flex items-center">
            <span className="text-2xl mr-2">🔍</span>
            <h3 className={`text-lg font-medium ${darkMode ? 'text-gray-100' : 'text-gray-900'}`}>
              {artifact.name}
            </h3>
          </div>
          {critique.score > 0 && renderScore(critique.score)}
        </div>

        {critique.idea_content && (
          <div className={`mb-4 p-3 rounded border ${
            darkMode ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-200'
          }`}>
            <h4 className={`text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              Original Idea
            </h4>
            <p className={darkMode ? 'text-gray-300' : 'text-gray-700'}>{critique.idea_content}</p>
          </div>
        )}

        <div className={`prose prose-sm max-w-none mb-4 ${darkMode ? 'prose-invert' : ''}`}>
          <div className={`whitespace-pre-wrap ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
            {critique.content}
          </div>
        </div>

        {critique.strengths?.length > 0 && sectionList(critique.strengths, 'Strengths')}
        {critique.weaknesses?.length > 0 && sectionList(critique.weaknesses, 'Weaknesses')}
        {critique.suggestions?.length > 0 && sectionList(critique.suggestions, 'Suggestions')}
      </div>
    </div>
  );
};
